import React, { useState, useEffect } from 'react';
import { MessageSquare, Plus, Trash2, Save, Send, Bot, Clock, Users, Zap, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import { PageTooltip } from '../components/TooltipSystem';

export default function AdminWhatsAppBot() {
  const [config, setConfig] = useState({
    enabled: false, phone_numbers: [], weekly_report_day: 'monday', weekly_report_time: '09:00',
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.get('/admin/bot-config')
      .then(r => {
        const c = r.data || {};
        setConfig({
          enabled: c.enabled || false,
          phone_numbers: c.phone_numbers || [],
          weekly_report_day: c.weekly_report_day || 'monday',
          weekly_report_time: c.weekly_report_time || '09:00',
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (override = null) => {
    setSaving(true);
    try {
      const payload = override ? { ...config, ...override } : config;
      await adminApi.put('/admin/bot-config', payload);
      if (!override) toast.success('Configurazione salvata!');
    } catch { toast.error('Errore nel salvataggio'); }
    finally { setSaving(false); }
  };

  /**
   * Toggling the master switch must persist IMMEDIATELY — otherwise the
   * user thinks the bot is off but navigating away and coming back
   * reloads the GET endpoint with the (still saved) "on" state.
   * We optimistically update local state, then PUT in the background
   * and rollback on failure.
   */
  const handleToggleEnabled = async () => {
    const next = !config.enabled;
    setConfig(c => ({ ...c, enabled: next }));
    setSaving(true);
    try {
      await adminApi.put('/admin/bot-config', { ...config, enabled: next });
      toast.success(next ? 'Bot WhatsApp attivato' : 'Bot WhatsApp disattivato');
    } catch {
      // Rollback on failure so the UI stays consistent with the server.
      setConfig(c => ({ ...c, enabled: !next }));
      toast.error('Errore nel salvataggio dello stato');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await adminApi.post('/admin/bot-test-report');
      toast.success(res.data.message || 'Report test inviato!');
    } catch (err) { toast.error(err.response?.data?.error || 'Errore invio'); }
    finally { setTesting(false); }
  };

  const addPhone = () => {
    if (config.phone_numbers.length >= 5) { toast.error('Massimo 5 numeri'); return; }
    setConfig(c => ({ ...c, phone_numbers: [...c.phone_numbers, { number: '', name: '' }] }));
  };

  const removePhone = (idx) => {
    setConfig(c => ({ ...c, phone_numbers: c.phone_numbers.filter((_, i) => i !== idx) }));
  };

  const updatePhone = (idx, field, value) => {
    setConfig(c => ({
      ...c,
      phone_numbers: c.phone_numbers.map((p, i) => i === idx ? { ...p, [field]: value } : p),
    }));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" /></div>;

  const DAYS = [
    { value: 'monday', label: 'Lunedi' }, { value: 'tuesday', label: 'Martedi' },
    { value: 'wednesday', label: 'Mercoledi' }, { value: 'thursday', label: 'Giovedi' },
    { value: 'friday', label: 'Venerdi' }, { value: 'saturday', label: 'Sabato' },
    { value: 'sunday', label: 'Domenica' },
  ];

  return (
    <div className="p-6 md:p-8 lg:p-10 space-y-6" data-testid="whatsapp-bot-page">
      <PageTooltip page="whatsapp-bot" />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-green-500 flex items-center justify-center">
              <Bot size={20} className="text-white" />
            </div>
            Bot WhatsApp Admin
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Report automatici, AI chat e attivazione iniziative direttamente su WhatsApp</p>
        </div>
        <button onClick={handleToggleEnabled}
          data-testid="bot-master-toggle"
          disabled={saving}
          className={`relative w-14 h-7 rounded-full transition-colors disabled:opacity-60 ${config.enabled ? 'bg-green-500' : 'bg-gray-300'}`}>
          <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform ${config.enabled ? 'translate-x-7' : 'translate-x-0.5'}`} />
        </button>
      </div>

      {!config.enabled && (
        <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center shadow-sm">
          <Bot size={48} className="text-slate-300 mx-auto mb-4" />
          <p className="text-[#1A1A1A] font-medium">Bot disattivato</p>
          <p className="text-sm text-[#64748B] mt-1">Attiva il toggle per configurare il bot WhatsApp admin.</p>
        </div>
      )}

      {config.enabled && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column: Config */}
          <div className="lg:col-span-2 space-y-6">
            {/* Phone numbers */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="bot-phones-section">
              <h2 className="font-semibold text-[#1A1A1A] text-sm flex items-center gap-2 mb-1">
                <Users size={16} className="text-green-600" /> Numeri destinatari
              </h2>
              <p className="text-xs text-[#64748B] mb-4">Questi numeri riceveranno i report e potranno interagire con il bot AI. Max 5.</p>
              <div className="space-y-3">
                {config.phone_numbers.map((entry, i) => (
                  <div key={i} className="flex gap-2 items-center" data-testid={`bot-phone-row-${i}`}>
                    <input type="text" placeholder="Nome (es. Marco)" value={entry.name}
                      onChange={e => updatePhone(i, 'name', e.target.value)}
                      data-testid={`bot-phone-name-${i}`}
                      className="flex-1 h-10 bg-white border border-slate-200 rounded-xl px-3 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-500/20 focus:outline-none" />
                    <input type="tel" placeholder="+39 333 1234567" value={entry.number}
                      onChange={e => updatePhone(i, 'number', e.target.value)}
                      data-testid={`bot-phone-number-${i}`}
                      className="flex-[1.5] h-10 bg-white border border-slate-200 rounded-xl px-3 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-500/20 focus:outline-none" />
                    <button onClick={() => removePhone(i)} className="w-8 h-8 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                {config.phone_numbers.length < 5 && (
                  <button onClick={addPhone} data-testid="add-bot-phone-btn"
                    className="text-green-600 text-xs font-medium flex items-center gap-1.5 hover:text-green-700 transition-colors mt-1">
                    <Plus size={14} /> Aggiungi numero
                  </button>
                )}
              </div>
            </div>

            {/* Schedule */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="bot-schedule-section">
              <h2 className="font-semibold text-[#1A1A1A] text-sm flex items-center gap-2 mb-1">
                <Clock size={16} className="text-green-600" /> Programmazione report
              </h2>
              <p className="text-xs text-[#64748B] mb-4">Scegli quando ricevere il ping settimanale su WhatsApp.</p>
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">Giorno</label>
                  <select value={config.weekly_report_day}
                    onChange={e => setConfig(c => ({ ...c, weekly_report_day: e.target.value }))}
                    data-testid="bot-report-day"
                    className="w-full h-10 bg-white border border-slate-200 rounded-xl px-3 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-500/20 focus:outline-none appearance-none">
                    {DAYS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">Orario</label>
                  <input type="time" value={config.weekly_report_time}
                    onChange={e => setConfig(c => ({ ...c, weekly_report_time: e.target.value }))}
                    data-testid="bot-report-time"
                    className="w-full h-10 bg-white border border-slate-200 rounded-xl px-3 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-500/20 focus:outline-none" />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button onClick={handleSave} disabled={saving} data-testid="save-bot-config-btn"
                className="flex-1 h-11 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors shadow-sm">
                <Save size={15} /> {saving ? 'Salvataggio...' : 'Salva configurazione'}
              </button>
              <button onClick={handleTest} disabled={testing || config.phone_numbers.length === 0} data-testid="test-report-btn"
                className="h-11 px-5 bg-white text-[#1A1A1A] border border-slate-200 text-sm font-medium rounded-xl hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2 transition-colors">
                <Send size={15} /> {testing ? 'Invio...' : 'Invia report test'}
              </button>
            </div>
          </div>

          {/* Right column: How it works */}
          <div className="space-y-6">
            {/* Flow diagram */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="bot-flow-section">
              <h2 className="font-semibold text-[#1A1A1A] text-sm flex items-center gap-2 mb-4">
                <Zap size={16} className="text-green-600" /> Come funziona
              </h2>
              <div className="space-y-3">
                {[
                  { step: '1', text: 'Ricevi il ping settimanale su WhatsApp', sub: 'Al giorno e ora che hai scelto' },
                  { step: '2', text: 'Rispondi REPORT', sub: 'Ricevi KPI + 3 proposte AI personalizzate' },
                  { step: '3', text: 'Rispondi A, B, C o TUTTO', sub: 'Le iniziative vengono create automaticamente' },
                ].map(s => (
                  <div key={s.step} className="flex gap-3 items-start">
                    <div className="w-7 h-7 rounded-full bg-green-50 text-green-600 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{s.step}</div>
                    <div>
                      <p className="text-sm font-medium text-[#1A1A1A]">{s.text}</p>
                      <p className="text-xs text-[#64748B]">{s.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Commands reference */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="bot-commands-section">
              <h2 className="font-semibold text-[#1A1A1A] text-sm flex items-center gap-2 mb-4">
                <HelpCircle size={16} className="text-green-600" /> Comandi disponibili
              </h2>
              <div className="space-y-2.5">
                {[
                  { cmd: 'REPORT', desc: 'KPI settimanale + proposte AI' },
                  { cmd: 'STATO', desc: 'Iniziative attive e andamento' },
                  { cmd: 'TREND', desc: 'Sparkline visite/clienti 4 settimane' },
                  { cmd: 'A / B / C', desc: 'Attiva una proposta AI' },
                  { cmd: 'TUTTO', desc: 'Attiva tutte le proposte' },
                  { cmd: 'AIUTO', desc: 'Lista comandi' },
                  { cmd: 'Domanda libera', desc: 'Chiedi qualsiasi cosa al bot AI' },
                ].map(c => (
                  <div key={c.cmd} className="flex items-center gap-3">
                    <code className="bg-slate-100 text-green-700 text-xs font-mono px-2 py-1 rounded-lg min-w-[90px] text-center">{c.cmd}</code>
                    <span className="text-xs text-[#64748B]">{c.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
